import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api from '../../lib/api';

const today = () => new Date().toISOString().split('T')[0];

const fmtRs = (v) =>
  'Rs. ' + Number(v || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
};

const ACCOUNT_OPTIONS = [
  { value: 'cash', label: 'Cash in Hand' },
  { value: 'bank', label: 'Bank Account' },
];

// ── small reusable components ──────────────────────────────────────────────

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#1a1a2e] mb-1">{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
  />
);

const Select = ({ value, onChange, children }) => (
  <select
    value={value}
    onChange={onChange}
    className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A7AE0]/20 focus:border-[#4A7AE0]"
  >
    {children}
  </select>
);

// ── main page ──────────────────────────────────────────────────────────────

const OwnerTransactions = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Inject form
  const [inAmount, setInAmount] = useState('');
  const [inAccount, setInAccount] = useState('cash');
  const [inDate, setInDate] = useState(today());
  const [inNote, setInNote] = useState('');
  const [inLoading, setInLoading] = useState(false);
  const [inMsg, setInMsg] = useState(null); // { type: 'success'|'error', text }

  // Withdraw form
  const [outAmount, setOutAmount] = useState('');
  const [outAccount, setOutAccount] = useState('cash');
  const [outDate, setOutDate] = useState(today());
  const [outNote, setOutNote] = useState('');
  const [outLoading, setOutLoading] = useState(false);
  const [outMsg, setOutMsg] = useState(null);

  // History
  const [history, setHistory] = useState({ transactions: [], total_injected: 0, total_withdrawn: 0 });
  const [histLoading, setHistLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/login'); return; }
    setUser(JSON.parse(storedUser));
    fetchHistory();
  }, [navigate]);

  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set('type_filter', typeFilter);
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const r = await api.get(`/capital/transactions?${params}`);
      setHistory(r.data);
    } catch (e) {
      console.error(e);
    }
    setHistLoading(false);
  }, [typeFilter, startDate, endDate]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // ── submit handlers ──────────────────────────────────────────────────────

  const handleInject = async () => {
    if (!inAmount || parseFloat(inAmount) <= 0) {
      setInMsg({ type: 'error', text: 'Please enter a valid amount greater than 0' });
      return;
    }
    setInLoading(true);
    setInMsg(null);
    try {
      const r = await api.post('/capital/inject', {
        amount: parseFloat(inAmount),
        account: inAccount,
        transaction_date: inDate,
        note: inNote.trim() || null,
      });
      setInMsg({ type: 'success', text: r.data.message });
      setInAmount('');
      setInNote('');
      setInDate(today());
      fetchHistory();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Something went wrong. Please try again.';
      setInMsg({ type: 'error', text: detail });
    }
    setInLoading(false);
  };

  const handleWithdraw = async () => {
    if (!outAmount || parseFloat(outAmount) <= 0) {
      setOutMsg({ type: 'error', text: 'Please enter a valid amount greater than 0' });
      return;
    }
    setOutLoading(true);
    setOutMsg(null);
    try {
      const r = await api.post('/capital/withdraw', {
        amount: parseFloat(outAmount),
        account: outAccount,
        transaction_date: outDate,
        note: outNote.trim() || null,
      });
      setOutMsg({ type: 'success', text: r.data.message });
      setOutAmount('');
      setOutNote('');
      setOutDate(today());
      fetchHistory();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Something went wrong. Please try again.';
      setOutMsg({ type: 'error', text: detail });
    }
    setOutLoading(false);
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex h-screen bg-[#f0f2f5]">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={toggleSidebar} />

        <main className="flex-1 overflow-y-auto p-6 sm:p-8">

          {/* Page title */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <Wallet className="w-7 h-7 text-[#1E40AF]" strokeWidth={1.5} />
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1a1a2e]">
                Owner Transactions
              </h1>
            </div>
            <p className="text-sm text-[#6b7280] mt-1 ml-10">
              Record money you put into the business or take out for personal use.
            </p>
          </div>

          {/* ── Two action cards ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">

            {/* CARD 1 — Put Money In */}
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-[#1E40AF] px-6 py-4 flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-white" strokeWidth={2} />
                <h2 className="text-white font-semibold text-lg">Put Money In</h2>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-[#6b7280]">
                  Owner adding personal money into the business
                </p>

                <Field label="Amount (Rs.)">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 50000"
                    value={inAmount}
                    onChange={(e) => setInAmount(e.target.value)}
                  />
                </Field>

                <Field label="Into which account">
                  <Select value={inAccount} onChange={(e) => setInAccount(e.target.value)}>
                    {ACCOUNT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>

                <Field label="Date">
                  <Input
                    type="date"
                    value={inDate}
                    onChange={(e) => setInDate(e.target.value)}
                  />
                </Field>

                <Field label="Note (optional)">
                  <Input
                    type="text"
                    placeholder="e.g. Personal savings added"
                    value={inNote}
                    onChange={(e) => setInNote(e.target.value)}
                  />
                </Field>

                {inMsg && (
                  <div className={`text-sm px-4 py-3 rounded-sm ${
                    inMsg.type === 'success'
                      ? 'bg-[#DBEAFE] text-[#1E40AF]'
                      : 'bg-[#FEE2E2] text-[#991B1B]'
                  }`}>
                    {inMsg.text}
                  </div>
                )}

                <button
                  onClick={handleInject}
                  disabled={inLoading}
                  className="w-full bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white font-medium py-2.5 rounded-sm transition-colors disabled:opacity-60"
                >
                  {inLoading ? 'Processing…' : 'Add to Business'}
                </button>
              </div>
            </div>

            {/* CARD 2 — Take Money Out */}
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-[#B45309] px-6 py-4 flex items-center gap-3">
                <TrendingDown className="w-5 h-5 text-white" strokeWidth={2} />
                <h2 className="text-white font-semibold text-lg">Take Money Out</h2>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-[#6b7280]">
                  Owner withdrawing money from the business
                </p>

                <Field label="Amount (Rs.)">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 10000"
                    value={outAmount}
                    onChange={(e) => setOutAmount(e.target.value)}
                  />
                </Field>

                <Field label="From which account">
                  <Select value={outAccount} onChange={(e) => setOutAccount(e.target.value)}>
                    {ACCOUNT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>

                <Field label="Date">
                  <Input
                    type="date"
                    value={outDate}
                    onChange={(e) => setOutDate(e.target.value)}
                  />
                </Field>

                <Field label="Note (optional)">
                  <Input
                    type="text"
                    placeholder="e.g. Personal expense"
                    value={outNote}
                    onChange={(e) => setOutNote(e.target.value)}
                  />
                </Field>

                {outMsg && (
                  <div className={`text-sm px-4 py-3 rounded-sm ${
                    outMsg.type === 'success'
                      ? 'bg-[#DBEAFE] text-[#1E40AF]'
                      : 'bg-[#FEE2E2] text-[#991B1B]'
                  }`}>
                    {outMsg.text}
                  </div>
                )}

                <button
                  onClick={handleWithdraw}
                  disabled={outLoading}
                  className="w-full bg-[#B45309] hover:bg-[#92400E] text-white font-medium py-2.5 rounded-sm transition-colors disabled:opacity-60"
                >
                  {outLoading ? 'Processing…' : 'Withdraw from Business'}
                </button>
              </div>
            </div>
          </div>

          {/* ── Summary totals ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5 flex items-center gap-4">
              <div className="p-3 bg-[#DBEAFE] rounded-full">
                <TrendingUp className="w-5 h-5 text-[#1E40AF]" strokeWidth={2} />
              </div>
              <div>
                <p className="text-xs text-[#6b7280] font-medium uppercase tracking-wide">Total Added</p>
                <p className="text-xl font-bold text-[#1E40AF]">{fmtRs(history.total_injected)}</p>
              </div>
            </div>
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5 flex items-center gap-4">
              <div className="p-3 bg-[#FEF3C7] rounded-full">
                <TrendingDown className="w-5 h-5 text-[#B45309]" strokeWidth={2} />
              </div>
              <div>
                <p className="text-xs text-[#6b7280] font-medium uppercase tracking-wide">Total Withdrawn</p>
                <p className="text-xl font-bold text-[#B45309]">{fmtRs(history.total_withdrawn)}</p>
              </div>
            </div>
          </div>

          {/* ── History table ── */}
          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm">
            {/* Filters */}
            <div className="p-4 border-b border-[#e5e7eb] flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="border border-[#e5e7eb] rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#4A7AE0]"
                >
                  <option value="">All</option>
                  <option value="injection">Money Added</option>
                  <option value="drawing">Money Withdrawn</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-[#e5e7eb] rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#4A7AE0]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-[#e5e7eb] rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#4A7AE0]"
                />
              </div>
              <button
                onClick={fetchHistory}
                className="flex items-center gap-1.5 border border-[#e5e7eb] rounded-sm px-3 py-1.5 text-sm text-[#6b7280] hover:bg-[#f9fafb] transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </button>
            </div>

            {/* Table */}
            {histLoading ? (
              <div className="py-12 text-center text-[#6b7280] text-sm">Loading transactions…</div>
            ) : history.transactions.length === 0 ? (
              <div className="py-12 text-center text-[#6b7280] text-sm">
                No transactions recorded yet. Use the cards above to get started.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                    <tr>
                      <th className="py-3 px-4 text-left">Date</th>
                      <th className="py-3 px-4 text-left">Type</th>
                      <th className="py-3 px-4 text-right">Amount</th>
                      <th className="py-3 px-4 text-left">Account</th>
                      <th className="py-3 px-4 text-left">Note</th>
                      <th className="py-3 px-4 text-left">Posted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]">
                        <td className="py-3 px-4 text-[#1a1a2e]">{fmtDate(tx.date)}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            tx.reference_type === 'capital_injection'
                              ? 'bg-[#DBEAFE] text-[#1E40AF]'
                              : 'bg-[#FEF3C7] text-[#92400E]'
                          }`}>
                            {tx.reference_type === 'capital_injection'
                              ? <TrendingUp className="w-3 h-3" />
                              : <TrendingDown className="w-3 h-3" />}
                            {tx.type}
                          </span>
                        </td>
                        <td className={`py-3 px-4 text-right font-semibold ${
                          tx.reference_type === 'capital_injection' ? 'text-[#1E40AF]' : 'text-[#B45309]'
                        }`}>
                          {fmtRs(tx.amount)}
                        </td>
                        <td className="py-3 px-4 text-[#6b7280]">{tx.account}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{tx.note || '—'}</td>
                        <td className="py-3 px-4 text-[#6b7280]">{tx.posted_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </main>
      </div>
    </div>
  );
};

export default OwnerTransactions;
